import { useEffect, useState } from "react";
import { Loader2, MailWarning, Save, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { errMsg } from "../ui";

/** Who gets the problems e-mail — active staff only. */
export function SyncRecipientsCard({ d, reload }) {
  const [picked, setPicked] = useState(d.recipients);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (d.can_edit) api.get("/billing-sync/autosync/staff").then(({ data }) => setStaff(data.users)).catch(() => setStaff([]));
  }, [d.can_edit]);
  const ids = new Set(picked.map((u) => u.user_id));
  const s = q.trim().toLowerCase();
  const matches = s ? staff.filter((u) => !ids.has(u.user_id) && `${u.name} ${u.email}`.toLowerCase().includes(s)).slice(0, 6) : [];
  const dirty = picked.map((u) => u.user_id).sort().join() !== d.recipients.map((u) => u.user_id).sort().join();
  const save = async () => {
    setBusy(true);
    try { await api.put("/billing-sync/autosync/recipients", { user_ids: picked.map((u) => u.user_id) }); toast.success("Recipients saved"); reload(); }
    catch (e) { toast.error(errMsg(e, "Could not save the recipients")); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5" data-testid="tis-recipients">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400"><MailWarning className="h-3.5 w-3.5" /> Problems e-mail</div>
      <p className="mt-1 text-xs text-slate-500">Sent only when a change failed or the preview found problems not reported before (missing customer, no plan, missing product…). Active staff only.</p>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="tis-recipient-list">
        {!picked.length && <span className="text-xs italic text-slate-400">Nobody yet — problems won't be e-mailed.</span>}
        {picked.map((u) => (
          <span key={u.user_id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700" data-testid={`tis-recipient-${u.user_id}`}>
            {u.name || u.email}<span className="text-slate-400">{u.email}</span>
            {d.can_edit && <button type="button" onClick={() => setPicked(picked.filter((x) => x.user_id !== u.user_id))} className="text-slate-400 hover:text-red-600" data-testid={`tis-recipient-remove-${u.user_id}`}><X className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {d.can_edit && (
        <div className="relative mt-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a staff member — type a name or email" className="h-9 text-sm" data-testid="tis-recipient-search" />
          {matches.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white p-1 shadow-lg" data-testid="tis-recipient-matches">
              {matches.map((u) => (
                <button key={u.user_id} type="button" onClick={() => { setPicked([...picked, u]); setQ(""); }} data-testid={`tis-recipient-add-${u.user_id}`}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                  <span>{u.name}</span><span className="text-xs text-slate-400">{u.email}</span></button>
              ))}
            </div>
          )}
          <Button size="sm" className="mt-3" disabled={!dirty || busy} onClick={save} data-testid="tis-recipients-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save recipients</Button>
        </div>
      )}
    </Card>
  );
}
